import { fmtKeyToSymbol, type KeyName } from '@/lib/key';
import { normalizeShortcutCombo } from '@/lib/keyboard-shortcuts';

import {
  getShortcutDefinition,
  SHORTCUT_DEFINITIONS,
  type ShortcutId,
} from './shortcuts.registry';

import type { Keys } from 'react-hotkeys-hook';

const modifierKeys = new Set([
  'alt',
  'control',
  'ctrl',
  'meta',
  'mod',
  'shift',
]);

const recordedKeyAliases: Record<string, string> = {
  control: 'ctrl',
  option: 'alt',
};

export function parseShortcutString(shortcut: string): KeyName[] {
  return shortcut.toLowerCase().split('+') as KeyName[];
}

export function formatShortcutKeys(
  keys: readonly KeyName[] | undefined
): string | undefined {
  if (!keys?.length) return undefined;

  const symbols = keys.map((key) => fmtKeyToSymbol(key));
  return symbols.reduce((label, symbol, index) => {
    const separator =
      symbols[index - 1].length > 1 || symbol.length > 1 ? '+' : '';
    return `${label}${separator}${symbol}`;
  });
}

function splitShortcutCombos(keys: Keys): string[] {
  const combos = typeof keys === 'string' ? keys.split(',') : keys;
  return combos.map((combo) => combo.trim()).filter(Boolean);
}

export function normalizeComboForConflict(combo: string): string {
  return normalizeShortcutCombo(combo);
}

export function recordedKeysToCombo(keys: Set<string>): string | null {
  const comboKeys = Array.from(keys)
    .map((key) => recordedKeyAliases[key] ?? key)
    .filter(Boolean);

  if (!comboKeys.some((key) => !modifierKeys.has(key))) return null;

  return comboKeys.join('+');
}

function shortcutConflictScope(shortcutId: ShortcutId): 'app' | 'hummingbird' {
  return shortcutId.startsWith('hummingbird.') ? 'hummingbird' : 'app';
}

export function findShortcutConflict(
  combo: string,
  currentShortcutId: ShortcutId,
  getShortcutCombo: (shortcutId: ShortcutId) => string | undefined,
  isShortcutEnabled: (shortcutId: ShortcutId) => boolean = () => true
): ShortcutId | undefined {
  const nextCombo = normalizeComboForConflict(combo);
  const currentConflictScope = shortcutConflictScope(currentShortcutId);
  const shortcutIds = Object.keys(SHORTCUT_DEFINITIONS) as ShortcutId[];

  return shortcutIds.find((shortcutId) => {
    if (shortcutId === currentShortcutId) return false;
    if (!isShortcutEnabled(shortcutId)) return false;

    if (shortcutConflictScope(shortcutId) !== currentConflictScope) {
      return false;
    }

    const definition = getShortcutDefinition(shortcutId);
    const customCombo = getShortcutCombo(shortcutId);
    const shortcutCombos = customCombo
      ? [customCombo]
      : splitShortcutCombos(definition.defaultCombo);

    return shortcutCombos.some(
      (shortcutCombo) => normalizeComboForConflict(shortcutCombo) === nextCombo
    );
  });
}
